import { TranslateService } from '@ngx-translate/core';

export const PERMISSION_LABEL_KEYS: Record<string, string> = {
  'company.dashboard.read': 'PERMISSIONS.COMPANY_DASHBOARD_READ',
  'company.company_profile.read': 'PERMISSIONS.COMPANY_PROFILE_READ',
  'company.properties.write': 'PERMISSIONS.COMPANY_PROPERTIES_WRITE',
  'company.properties.read': 'PERMISSIONS.COMPANY_PROPERTIES_READ',
  'company.properties.delete': 'PERMISSIONS.COMPANY_PROPERTIES_DELETE',
  'company.properties.import': 'PERMISSIONS.COMPANY_PROPERTIES_IMPORT',
  'company.templates.read': 'PERMISSIONS.COMPANY_TEMPLATES_READ',
  'company.contacts.write': 'PERMISSIONS.COMPANY_CONTACTS_WRITE',
  'company.contacts.read': 'PERMISSIONS.COMPANY_CONTACTS_READ',
  'company.contacts.delete': 'PERMISSIONS.COMPANY_CONTACTS_DELETE',
  'company.announcements.read': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_READ',
  'company.announcements.write': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_WRITE',
  'company.announcements.send': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_SEND',
  'company.announcements.delete': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_DELETE',
  'company.company_profile.write': 'PERMISSIONS.COMPANY_PROFILE_WRITE',
  'company.property_documents.read': 'PERMISSIONS.COMPANY_PROPERTY_DOCUMENTS_READ',
  'company.property_documents.write': 'PERMISSIONS.COMPANY_PROPERTY_DOCUMENTS_WRITE',
  'company.property_documents.delete': 'PERMISSIONS.COMPANY_PROPERTY_DOCUMENTS_DELETE',
  'company.emergency_instructions.write': 'PERMISSIONS.COMPANY_EMERGENCY_INSTRUCTIONS_WRITE',
  'company.emergency_instructions.delete': 'PERMISSIONS.COMPANY_EMERGENCY_INSTRUCTIONS_DELETE',
  'company.ratings.read': 'PERMISSIONS.COMPANY_RATINGS_READ',
  'company.hospitality_ai.read': 'PERMISSIONS.COMPANY_HOSPITALITY_AI_READ',
  'company.templates.write': 'PERMISSIONS.COMPANY_TEMPLATES_WRITE',
  'company.qr_poster_texts.read': 'PERMISSIONS.COMPANY_QR_POSTER_TEXTS_READ',
  'company.qr_poster_texts.write': 'PERMISSIONS.COMPANY_QR_POSTER_TEXTS_WRITE',
  'company.hospitality_ai.qa.write': 'PERMISSIONS.COMPANY_HOSPITALITY_AI_QA_WRITE',
  'company.integrations.read': 'PERMISSIONS.COMPANY_INTEGRATIONS_READ',
  'company.integrations.write': 'PERMISSIONS.COMPANY_INTEGRATIONS_WRITE',
  'company.integrations.credentials.write': 'PERMISSIONS.COMPANY_INTEGRATIONS_CREDENTIALS_WRITE',
  'company.integrations.run': 'PERMISSIONS.COMPANY_INTEGRATIONS_RUN',
  'company.integrations.delete': 'PERMISSIONS.COMPANY_INTEGRATIONS_DELETE',
  'company.users.read': 'PERMISSIONS.COMPANY_USERS_READ',
  'company.users.invite': 'PERMISSIONS.COMPANY_USERS_INVITE',
  'company.users.update': 'PERMISSIONS.COMPANY_USERS_UPDATE',
  'company.users.deactivate': 'PERMISSIONS.COMPANY_USERS_DEACTIVATE',
  'company.users.remove': 'PERMISSIONS.COMPANY_USERS_REMOVE',
};

const ROLE_PARAM_KEYS = new Set(['previousRole', 'newRole', 'subjectRole', 'actorRole', 'userRole']);
const PLAN_PARAM_KEYS = new Set(['plan', 'previousPlan', 'newPlan']);
const PROVIDER_KEYS = new Set(['provider']);
const DATE_PARAM_KEYS = new Set(['issueDate', 'dueDate', 'activationDate']);
const PERMISSION_SLOT_RE = /^(addedPermission|removedPermission)1$/;

const VALID_AUDIENCES = new Set(['manager', 'subject', 'requester', 'general']);
const VALID_VARIANTS = new Set(['role', 'permissions', 'role_and_permissions']);

const ROLE_LABEL_KEY_MAP: Record<string, string> = {
  company_admin: 'ROLES.COMPANY_ADMIN',
  editor: 'ROLES.EDITOR',
  viewer: 'ROLES.VIEWER',
};

const PROVIDER_LABEL_KEY_MAP: Record<string, string> = {
  naviga: 'NOTIFICATIONS.PROVIDERS.NAVIGA',
  haustec: 'NOTIFICATIONS.PROVIDERS.HAUSTEC',
};

const DOCUMENT_TYPE_KEY_MAP: Record<string, string> = {
  contract: 'ACCOUNT_SETTINGS.BILLING.CONTRACT',
  invoice: 'ACCOUNT_SETTINGS.BILLING.INVOICE',
};

const SUPPORTED_NOTIFICATION_CURRENCIES = new Set(['EUR']);

const BILLING_CHANGE_FIELD_LABEL_KEYS: Record<string, string> = {
  document_type: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.DOCUMENT_TYPE',
  title: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.TITLE',
  document_number: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.DOCUMENT_NUMBER',
  status: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.STATUS',
  amount: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.AMOUNT',
  issue_date: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.ISSUE_DATE',
  due_date: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.DUE_DATE',
  contract_start_date: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.CONTRACT_START_DATE',
  financial_details: 'NOTIFICATIONS.BILLING_CHANGE_FIELDS.FINANCIAL_DETAILS',
};

export function resolveRoleLabel(roleKey: string, translate: TranslateService): string {
  if (!roleKey) return '';
  const labelKey = ROLE_LABEL_KEY_MAP[roleKey];
  if (labelKey) {
    const label = translate.instant(labelKey);
    if (label !== labelKey) return label;
  }
  return '';
}

export function resolvePlanLabel(planKey: string, translate: TranslateService): string {
  if (!planKey) return '';
  const upper = planKey.toUpperCase();
  const key = `PLAN.${upper}`;
  const label = translate.instant(key);
  if (label !== key) return label;
  return '';
}

export function resolveProviderLabel(providerKey: string, translate: TranslateService): string {
  if (!providerKey) return '';
  const labelKey = PROVIDER_LABEL_KEY_MAP[providerKey];
  if (labelKey) {
    const label = translate.instant(labelKey);
    if (label !== labelKey) return label;
  }
  return translate.instant('NOTIFICATIONS.LABELS.GENERIC_PROVIDER');
}

export function resolveDocumentTypeLabel(documentType: string, translate: TranslateService): string {
  if (!documentType) return '';
  const labelKey = DOCUMENT_TYPE_KEY_MAP[documentType];
  if (labelKey) {
    const label = translate.instant(labelKey);
    if (label !== labelKey) return label;
  }
  return translate.instant('NOTIFICATIONS.LABELS.GENERIC_DOCUMENT');
}

export function resolvePermissionLabel(permissionKey: string, translate: TranslateService): string {
  if (!permissionKey) return '';
  const labelKey = PERMISSION_LABEL_KEYS[permissionKey];
  if (labelKey) {
    const label = translate.instant(labelKey);
    if (label !== labelKey) return label;
  }
  return '';
}

function safeFormatCurrency(
  amount: string | undefined,
  currency: string | undefined,
  locale: string,
): string {
  try {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return '';
    const currencyCode = String(currency || '').trim().toUpperCase();
    if (!SUPPORTED_NOTIFICATION_CURRENCIES.has(currencyCode)) return '';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(numericAmount);
  } catch {
    return '';
  }
}

function safeFormatDate(dateStr: string | undefined, locale: string): string {
  try {
    if (!dateStr) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!match) return '';
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
      return '';
    }
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    return '';
  }
}

export function isValidAudience(value: string | undefined): boolean {
  return !!value && VALID_AUDIENCES.has(value);
}

export function isValidVariant(value: string | undefined): boolean {
  return !!value && VALID_VARIANTS.has(value);
}

function hasOwn(value: Record<string, string>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

export function resolveIdentityDisplay(
  displayName: string | undefined,
  roleKey: string | undefined,
  translate: TranslateService,
  fallbackKey = 'NOTIFICATIONS.IDENTITY.TEAM_MEMBER',
): string {
  if (displayName?.trim()) return displayName.trim();
  if (roleKey) {
    if (roleKey === 'Admin') {
      return translate.instant('NOTIFICATIONS.IDENTITY.PLATFORM_ADMIN');
    }
    const label = resolveRoleLabel(roleKey, translate);
    if (label) return label;
  }
  return translate.instant(fallbackKey);
}

export function resolveNotificationParams(
  itemParams: Record<string, string> | undefined,
  translate: TranslateService,
): Record<string, string> {
  const raw = { ...(itemParams || {}) };
  const locale = translate.currentLang === 'de' ? 'de-DE' : 'en-US';

  if (hasOwn(raw, 'actorDisplayName') || hasOwn(raw, 'actorRole')) {
    raw['actorIdentity'] = resolveIdentityDisplay(
      raw['actorDisplayName'],
      raw['actorRole'],
      translate,
      'NOTIFICATIONS.IDENTITY.TEAM_MEMBER',
    );
  }
  if (hasOwn(raw, 'subjectDisplayName') || hasOwn(raw, 'subjectRole') || hasOwn(raw, 'memberDisplayName')) {
    raw['subjectIdentity'] = resolveIdentityDisplay(
      raw['subjectDisplayName'] || raw['memberDisplayName'],
      raw['subjectRole'],
      translate,
      'NOTIFICATIONS.IDENTITY.TEAM_MEMBER',
    );
  }
  if (hasOwn(raw, 'userDisplayName') || hasOwn(raw, 'userRole')) {
    raw['userIdentity'] = resolveIdentityDisplay(
      raw['userDisplayName'],
      raw['userRole'],
      translate,
      'NOTIFICATIONS.IDENTITY.COMPANY_USER',
    );
  }

  if (raw['amount'] && raw['currency']) {
    const fmt = safeFormatCurrency(raw['amount'], raw['currency'], locale);
    if (fmt) raw['formattedAmount'] = fmt;
  }
  if (raw['previousAmount'] && raw['currency']) {
    const fmt = safeFormatCurrency(raw['previousAmount'], raw['currency'], locale);
    if (fmt) raw['formattedPreviousAmount'] = fmt;
  }
  if (raw['newAmount'] && raw['currency']) {
    const fmt = safeFormatCurrency(raw['newAmount'], raw['currency'], locale);
    if (fmt) raw['formattedNewAmount'] = fmt;
  }

  const ALL_DATE_PARAM_KEYS = [...DATE_PARAM_KEYS, 'previousDueDate', 'newDueDate', 'previousIssueDate', 'newIssueDate', 'previousContractStartDate', 'newContractStartDate'];
  for (const dk of ALL_DATE_PARAM_KEYS) {
    if (raw[dk]) {
      const fmt = safeFormatDate(raw[dk], locale);
      if (fmt) raw[`formatted${dk.charAt(0).toUpperCase() + dk.slice(1)}`] = fmt;
    }
  }

  for (const key of Object.keys(raw)) {
    if (ROLE_PARAM_KEYS.has(key) && raw[key]) {
      raw[key] = resolveRoleLabel(raw[key], translate);
    }
    if (PLAN_PARAM_KEYS.has(key) && raw[key]) {
      raw[key] = resolvePlanLabel(raw[key], translate);
    }
    if (PROVIDER_KEYS.has(key) && raw[key]) {
      raw[key] = resolveProviderLabel(raw[key], translate);
    }

    if ((key === 'previousDocumentType' || key === 'newDocumentType') && raw[key]) {
      raw[key] = resolveDocumentTypeLabel(raw[key], translate);
    }
    if ((key === 'previousStatus' || key === 'newStatus') && raw[key]) {
      const statusUpper = raw[key].toUpperCase();
      const statusKey = `BILLING.STATUS.${statusUpper}`;
      const statusLabel = translate.instant(statusKey);
      if (statusLabel !== statusKey) raw[key] = statusLabel;
    }
    if (/^changedField[123]$/.test(key) && raw[key]) {
      const labelKey = BILLING_CHANGE_FIELD_LABEL_KEYS[raw[key]];
      if (labelKey) {
        const label = translate.instant(labelKey);
        if (label !== labelKey) raw[key + 'Label'] = label;
      }
    }
    if (PERMISSION_SLOT_RE.test(key) && raw[key]) {
      const label = resolvePermissionLabel(raw[key], translate);
      if (label) raw[key + 'Label'] = label;
    }
  }

  raw['documentIdentity'] = translate.instant('NOTIFICATIONS.LABELS.GENERIC_DOCUMENT');

  const originalDocumentType = raw['documentType'] || '';
  const resolvedDocumentType = originalDocumentType
    ? resolveDocumentTypeLabel(originalDocumentType, translate)
    : '';

  if (resolvedDocumentType) {
    raw['documentType'] = resolvedDocumentType;
    const docNum = (raw['documentNumber'] || '').trim();
    const docTitle = (raw['title'] || raw['documentTitle'] || '').trim();
    if (docNum) {
      raw['documentIdentity'] = `${resolvedDocumentType} ${docNum}`;
    } else if (docTitle) {
      raw['documentIdentity'] = `${resolvedDocumentType} "${docTitle}"`;
    } else {
      raw['documentIdentity'] = resolvedDocumentType;
    }
  }

  return raw;
}
